import type { CSSProperties } from "react";

import { ChartHoverTooltip, useChartHover } from "./chart-hover-tooltip";
import { formatBucketTime, formatCompactCount } from "./chart-format";
import { ChartLegend } from "./chart-legend";
import type { BucketFormatter, ChartSeries, ValueFormatter } from "./chart-types";
import { cn } from "./cn";
import "./chart-bar.css";

export interface BarChartProps {
  title: string;
  buckets: string[];
  /** 1..8 series, index-aligned to `buckets`. */
  series: ChartSeries[];
  /** From useCategoricalColors(series.map(s => s.key)), or explicit overrides. */
  colors: Map<string, string>;
  /** "single" (one bar per bucket, the launch-day look, peak bar direct
   *  labeled) requires exactly one series. "grouped" draws N bars per bucket
   *  side by side. "stacked" draws segments with a 2px surface-color GAP
   *  between them (never a hairline border — the dataviz mark spec). Defaults
   *  to "single" for one series, "grouped" for 2+. */
  layout?: "single" | "grouped" | "stacked";
  formatBucket?: BucketFormatter;
  formatValue?: ValueFormatter;
  /** Caption suffix after the total figure, e.g. "in 24h". */
  windowLabel: string;
  /** The caption's total, when summing every series' values would be WRONG —
   *  e.g. a per-bucket DISTINCT-something series that double-counts a subject
   *  across buckets. Defaults to the sum of every series' values. */
  total?: number;
  /** Override the auto-built <svg role="img"> accessible name. */
  ariaLabel?: string;
  /** Opens the drilldown behind this chart's total. */
  onOpen?: () => void;
  /** Opt-in floating tooltip that follows the cursor and shows every
   *  series' value for the hovered bucket at once (mouse only — see
   *  chart-hover-tooltip.tsx). Defaults to `false`: this primitive also
   *  renders web/admin/seller/admin-internal's existing dashboards, whose
   *  native-`<title>`-only hover behavior stays byte-identical unless a
   *  caller opts in. The native `<title>` on every bar is never removed —
   *  it's the accessible-name path for a mouse+AT combination, since the
   *  bars carry no keyboard focus stop either way. */
  hoverTooltip?: boolean;
  /** The right-hand axis label. Defaults to "now", which is correct for the
   *  live ops charts this primitive was built for (a rolling window whose
   *  last bucket really IS the present moment) and a false claim on any chart
   *  over a historical or reader-chosen window, where it labels the final
   *  bucket as the present. Pass that bucket's own formatted label there
   *  instead.
   *
   *  It is a real defect, not a cosmetic one: the BI app's charts render an
   *  arbitrary picked window, so an unset label told every reader the last
   *  bar was happening right now, whatever date it actually covered. */
  axisEndLabel?: string;
  /** Caps how wide one bar may be drawn, in svg units (the plot is 480 wide).
   *  Unset means a bar fills its whole step, which is right for the dense
   *  charts this primitive was built for and degenerate at low bucket counts:
   *  a single bucket makes `step` the entire plot, so the "bar" renders as a
   *  filled rectangle spanning the chart and stops reading as a bar at all.
   *  Bars stay centered in their step (and a group stays centered in its), so
   *  capping narrows them rather than shifting them off their bucket. */
  maxBarWidth?: number;
  className?: string;
}

const GROUP_GAP = 2;
const STACK_GAP = 2;

/**
 * Rounded-bar chart. Single-series: one bar per bucket, only the peak bar
 * direct-labeled, every bar carries a hover tooltip (the launch-day look,
 * byte-identical). Grouped/stacked (2+ series): a legend renders
 * automatically, and no bar is direct-labeled — with several series past one
 * bucket a peak label has no single unambiguous place to sit, so the hover
 * tooltip + legend carry the value instead.
 */
export function BarChart({
  title,
  buckets,
  series,
  colors,
  layout,
  formatBucket = formatBucketTime,
  axisEndLabel = "now",
  maxBarWidth,
  formatValue = formatCompactCount,
  windowLabel,
  total: totalOverride,
  ariaLabel,
  onOpen,
  hoverTooltip = false,
  className,
}: BarChartProps) {
  const resolvedLayout = layout ?? (series.length === 1 ? "single" : "grouped");
  const W = 480;
  const H = 120;
  const PAD_TOP = 22;
  const PAD_BOTTOM = 18;
  const plotH = H - PAD_TOP - PAD_BOTTOM;
  const baseY = H - PAD_BOTTOM;
  const n = Math.max(buckets.length, 1);
  const step = W / n;

  const total =
    totalOverride ?? series.reduce((sum, s) => sum + s.values.reduce((a, b) => a + b, 0), 0);
  const resolvedAriaLabel = ariaLabel ?? `${title}, ${formatValue(total)} ${windowLabel}`;

  // Every layout shares the same plot geometry (no left/right padding, `n`
  // equal-width steps) so one hover mapping covers single/grouped/stacked
  // alike: hovering ANY x-position within a bucket's step shows every
  // series' value for that bucket at once (the whole point of a multi-series
  // tooltip — see the design doc's quality bar).
  const hover = useChartHover(buckets.length, "step", hoverTooltip);
  const hoveredValues =
    hover.index !== null
      ? series.map((s) => ({ s, v: s.values[hover.index as number] ?? 0 }))
      : null;
  const tooltip =
    hover.index !== null && hoveredValues ? (
      <ChartHoverTooltip anchor={hover.anchor}>
        <span className="rc-chart-hover-tooltip__bucket">
          {formatBucket(buckets[hover.index] ?? "", hover.index)}
        </span>
        {hoveredValues.map(({ s, v }) => (
          <span key={s.key} className="rc-chart-hover-tooltip__row">
            <span
              className="rc-chart-hover-tooltip__swatch"
              aria-hidden="true"
              style={{ background: colors.get(s.key) ?? "var(--rc-chart-other)" }}
            />
            <span className="rc-chart-hover-tooltip__label">{s.label}</span>
            <span className="rc-chart-hover-tooltip__value">{formatValue(v)}</span>
          </span>
        ))}
      </ChartHoverTooltip>
    ) : null;

  const caption = (accent: string | undefined) => (
    <figcaption className="rc-chart__caption">
      {accent ? <span className="rc-chart__swatch" aria-hidden="true" /> : null}
      <span className="rc-chart__title">{title}</span>
      {onOpen ? (
        <button
          type="button"
          className="rc-chart__total"
          onClick={onOpen}
          title={`See the rows behind ${title.toLowerCase()}`}
        >
          {formatValue(total)} {windowLabel}
        </button>
      ) : (
        <span className="rc-chart__total">
          {formatValue(total)} {windowLabel}
        </span>
      )}
    </figcaption>
  );

  if (resolvedLayout === "single") {
    const only = series[0];
    const values = only?.values ?? [];
    const accent = only ? (colors.get(only.key) ?? "var(--rc-chart-other)") : undefined;
    const barW = Math.min(Math.max(step - 3, 2), maxBarWidth ?? Infinity);
    const max = Math.max(...values, 1);
    const peak = values.indexOf(Math.max(...values));
    return (
      <figure
        className={cn("rc-chart", "rc-chart--bars", className)}
        style={{ "--rc-chart-accent": accent } as CSSProperties}
      >
        {caption(accent)}
        <svg
          className="rc-chart__svg"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={resolvedAriaLabel}
          {...hover.handlers}
        >
          <line className="rc-chart__baseline" x1={0} y1={baseY} x2={W} y2={baseY} />
          {values.map((v, i) => {
            const h = Math.round((v / max) * plotH);
            const bx = i * step + (step - barW) / 2;
            const by = baseY - h;
            return (
              <g key={buckets[i] ?? i}>
                <rect
                  className="rc-chart__bar"
                  x={bx}
                  y={by}
                  width={barW}
                  height={Math.max(h, v > 0 ? 2 : 0)}
                  rx={3}
                >
                  <title>{`${formatBucket(buckets[i] ?? "", i)}: ${formatValue(v)}`}</title>
                </rect>
                {i === peak && v > 0 ? (
                  <text
                    className="rc-chart__peak"
                    x={Math.min(Math.max(bx + barW / 2, 14), W - 14)}
                    y={Math.max(by - 6, 12)}
                    textAnchor="middle"
                  >
                    {formatValue(v)}
                  </text>
                ) : null}
              </g>
            );
          })}
          <text className="rc-chart__axis" x={2} y={H - 4}>
            {formatBucket(buckets[0] ?? "", 0)}
          </text>
          <text className="rc-chart__axis" x={W - 2} y={H - 4} textAnchor="end">
            {axisEndLabel}
          </text>
        </svg>
        {tooltip}
      </figure>
    );
  }

  if (resolvedLayout === "grouped") {
    const max = Math.max(...series.flatMap((s) => s.values), 1);
    const groupW = Math.max(step - 6, series.length * 2);
    const barW = Math.min(
      Math.max((groupW - GROUP_GAP * (series.length - 1)) / series.length, 1),
      maxBarWidth ?? Infinity,
    );
    const offset = (step - (barW * series.length + GROUP_GAP * (series.length - 1))) / 2;
    return (
      <figure className={cn("rc-chart", "rc-chart--bars", className)}>
        {caption(undefined)}
        <ChartLegend series={series} colors={colors} />
        <svg
          className="rc-chart__svg"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={resolvedAriaLabel}
          {...hover.handlers}
        >
          <line className="rc-chart__baseline" x1={0} y1={baseY} x2={W} y2={baseY} />
          {buckets.map((bucket, i) => (
            <g key={bucket || i}>
              {series.map((s, si) => {
                const v = s.values[i] ?? 0;
                const h = Math.round((v / max) * plotH);
                const bx = i * step + offset + si * (barW + GROUP_GAP);
                const by = baseY - h;
                return (
                  <rect
                    key={s.key}
                    className="rc-chart__bar"
                    style={
                      {
                        "--rc-chart-accent": colors.get(s.key) ?? "var(--rc-chart-other)",
                      } as CSSProperties
                    }
                    x={bx}
                    y={by}
                    width={barW}
                    height={Math.max(h, v > 0 ? 2 : 0)}
                    rx={2}
                  >
                    <title>{`${formatBucket(bucket, i)} ${s.label}: ${formatValue(v)}`}</title>
                  </rect>
                );
              })}
            </g>
          ))}
          <text className="rc-chart__axis" x={2} y={H - 4}>
            {formatBucket(buckets[0] ?? "", 0)}
          </text>
          <text className="rc-chart__axis" x={W - 2} y={H - 4} textAnchor="end">
            {axisEndLabel}
          </text>
        </svg>
        {tooltip}
      </figure>
    );
  }

  // stacked
  const bucketSums = buckets.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
  const max = Math.max(...bucketSums, 1);
  const barW = Math.min(Math.max(step - 3, 2), maxBarWidth ?? Infinity);
  return (
    <figure className={cn("rc-chart", "rc-chart--bars", className)}>
      {caption(undefined)}
      <ChartLegend series={series} colors={colors} />
      <svg
        className="rc-chart__svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={resolvedAriaLabel}
        {...hover.handlers}
      >
        <line className="rc-chart__baseline" x1={0} y1={baseY} x2={W} y2={baseY} />
        {buckets.map((bucket, i) => {
          const bx = i * step + (step - barW) / 2;
          let cursor = baseY;
          return (
            <g key={bucket || i}>
              {series.map((s) => {
                const v = s.values[i] ?? 0;
                const h = Math.round((v / max) * plotH);
                if (h <= 0) return null;
                const by = cursor - h;
                cursor = by - STACK_GAP;
                return (
                  <rect
                    key={s.key}
                    className="rc-chart__bar"
                    style={
                      {
                        "--rc-chart-accent": colors.get(s.key) ?? "var(--rc-chart-other)",
                      } as CSSProperties
                    }
                    x={bx}
                    y={by}
                    width={barW}
                    height={h}
                    rx={2}
                  >
                    <title>{`${formatBucket(bucket, i)} ${s.label}: ${formatValue(v)}`}</title>
                  </rect>
                );
              })}
            </g>
          );
        })}
        <text className="rc-chart__axis" x={2} y={H - 4}>
          {formatBucket(buckets[0] ?? "", 0)}
        </text>
        <text className="rc-chart__axis" x={W - 2} y={H - 4} textAnchor="end">
          {axisEndLabel}
        </text>
      </svg>
      {tooltip}
    </figure>
  );
}
